// src/pages/workflow-builder/WorkflowBuilder.js
import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
} from 'react-flow-renderer';
import NodeConfigModal from './NodeConfigModal';
import {
    DefaultNode,
    CallNode,
    SMSNode,
    EmailNode,
    ConditionalNode,
    DelayNode,
} from './nodeTypes';
import { getWorkflow, saveWorkflow, updateWorkflow } from '../../services/workflowService';
import './WorkflowBuilder.css';

const nodeTypes = {
    defaultNode: DefaultNode,
    callNode: CallNode,
    smsNode: SMSNode,
    emailNode: EmailNode,
    conditionalNode: ConditionalNode,
    delayNode: DelayNode,
};

let idCounter = 100;
const getId = () => `node_${idCounter++}`;

const WorkflowBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const campaignId = queryParams.get('campaignId');

    const [flowName, setFlowName] = useState('');
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentCampaignId, setCurrentCampaignId] = useState(campaignId);

    // Load existing workflow if editing
    useEffect(() => {
        const loadWorkflow = async () => {
            if (id && id !== 'new') {
                setLoading(true);
                try {
                    const response = await getWorkflow(id);
                    const { workflow } = response.data;
                    setFlowName(workflow.name);
                    setCurrentCampaignId(workflow.campaignId); // Store the campaign ID from the workflow

                    // Add the deleteNode function to each node's data
                    const loadedNodes = workflow.nodes.map((n) => ({
                        ...n,
                        data: {
                            ...n.data,
                            onDelete: deleteNode,
                        },
                    }));

                    setNodes(loadedNodes);
                    setEdges(workflow.edges);
                } catch (error) {
                    console.error('Error loading workflow:', error);
                    setError('Failed to load workflow');
                } finally {
                    setLoading(false);
                }
            }
        };

        loadWorkflow();
    }, [id, setNodes, setEdges]);

    // Delete node handler
    const deleteNode = useCallback(
        (nodeId) => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
        },
        [setNodes]
    );

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();
            if (!reactFlowInstance) return;

            const type = event.dataTransfer.getData('application/reactflow');
            if (!type) return;

            const position = reactFlowInstance.project({
                x: event.clientX,
                y: event.clientY,
            });

            const newNode = {
                id: getId(),
                type,
                position,
                data: { label: `${type}`, subLabel: '', onDelete: deleteNode },
            };

            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance, setNodes, deleteNode]
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, type: 'straight' }, eds)),
        [setEdges]
    );

    const onNodeClick = useCallback((event, node) => {
        setSelectedNode(node);
        setShowModal(true);
    }, []);

    const handleUpdateNode = (id, updates) => {
        setNodes((nds) =>
            nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n))
        );
        setShowModal(false);
        setSelectedNode(null);
    };

    const handleSave = async () => {
        if (!flowName.trim()) {
            alert('Please enter a workflow name');
            return;
        }

        const effectiveCampaignId = currentCampaignId || campaignId;
        if (!effectiveCampaignId) {
            alert('Campaign ID is required');
            return;
        }

        const workflowData = {
            name: flowName,
            campaignId: effectiveCampaignId,
            nodes,
            edges,
        };

        try {
            if (id && id !== 'new') {
                await updateWorkflow(id, workflowData);
            } else {
                await saveWorkflow(workflowData);
            }
            // Navigate back to campaign overview
            navigate(`/campaigns/${effectiveCampaignId}/overview`);
        } catch (error) {
            console.error('Error saving workflow:', error);
            alert('Error saving workflow');
        }
    };

    const handleCancel = () => {
        // Use the effective campaign ID for navigation
        const effectiveCampaignId = currentCampaignId || campaignId;
        if (effectiveCampaignId) {
            navigate(`/campaigns/${effectiveCampaignId}/overview`);
        } else {
            navigate('/campaigns'); // Fallback to campaigns list if no campaign ID
        }
    };

    if (loading) {
        return <div>Loading workflow...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <ReactFlowProvider>
            <div className="workflow-builder-page">
                {/* Floating Sidebar */}
                <div className="workflow-sidebar">
                    <h3>Workflow Actions</h3>
                    <input
                        type="text"
                        placeholder="Search Actions..."
                        className="workflow-search"
                    />
                    <div
                        className="sidebar-item"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData('application/reactflow', 'callNode');
                            e.dataTransfer.effectAllowed = 'move';
                        }}
                    >
                        Call
                    </div>
                    <div
                        className="sidebar-item"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData('application/reactflow', 'smsNode');
                            e.dataTransfer.effectAllowed = 'move';
                        }}
                    >
                        SMS
                    </div>
                    <div
                        className="sidebar-item"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData('application/reactflow', 'emailNode');
                            e.dataTransfer.effectAllowed = 'move';
                        }}
                    >
                        Email
                    </div>
                    <div
                        className="sidebar-item"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData('application/reactflow', 'conditionalNode');
                            e.dataTransfer.effectAllowed = 'move';
                        }}
                    >
                        Conditional
                    </div>
                    <div
                        className="sidebar-item"
                        draggable
                        onDragStart={(e) => {
                            e.dataTransfer.setData('application/reactflow', 'delayNode');
                            e.dataTransfer.effectAllowed = 'move';
                        }}
                    >
                        Delay
                    </div>
                </div>
                <div className="workflow-main-area">
                    <div className="workflow-top-bar">
                        <input
                            className="flow-name-input"
                            value={flowName}
                            onChange={(e) => setFlowName(e.target.value)}
                            placeholder="Enter workflow name..."
                        />
                        <div className="workflow-top-buttons">
                            <button className="purple-btn" onClick={handleSave}>
                                Save
                            </button>
                            <button
                                className="purple-btn exit"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    <div className="workflow-canvas" onDrop={onDrop} onDragOver={onDragOver}>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            nodeTypes={nodeTypes}
                            onNodeClick={onNodeClick}
                            fitView
                            snapToGrid={true}
                            snapGrid={[15, 15]}
                            onInit={setReactFlowInstance}
                            defaultEdgeOptions={{ type: 'straight' }}
                        >
                            <Controls />
                        </ReactFlow>
                    </div>
                </div>
            </div>
            {showModal && selectedNode && (
                <NodeConfigModal
                    node={selectedNode}
                    onClose={() => setShowModal(false)}
                    onSave={handleUpdateNode}
                />
            )}
        </ReactFlowProvider>
    );
};

export default WorkflowBuilder;